import { Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import {
  ControlRow,
  SelectOption,
  emptyFilledFilters,
  LookupValue
} from 'store/ducks/InternalControls/types';
import CustomSelect from 'components/CustomSelect';
import CustomFileUpload from 'components/CustomFileUpload';
import dayjs from 'dayjs';
import CustomDatePicker from 'components/CustomDatePicker';
import Helpers from 'utils/Helpers';
import {
  base_fields_control_test_design,
  instance_fields_control_test_design
} from './riskControlFieldsToDisable';
import * as Styled from '../pages/InternalControlsMain/RiskControl.styled';

export const getDesignEffectivenessColumns = (
  testingMethod: SelectOption[],
  approvers: SelectOption[],
  controls: ControlRow[],
  performerToD: SelectOption[],
  designEffectiveness: SelectOption[],
  testObjectiveness: SelectOption[],
  setControls: (controls: ControlRow[]) => void,
  trackingSetControls: (controls: ControlRow[]) => void,
  newControlIds?: Set<number>,
  isScopedView?: boolean,
  t?: (key: string) => string
): ColumnsType<ControlRow> => {
  const shouldDisable = (record: ControlRow, fieldKey: string) => {
    if (record.notUsed) return true;

    if (isScopedView) {
      return base_fields_control_test_design.has(fieldKey);
    }

    return instance_fields_control_test_design.has(fieldKey);
  };

  return [
    {
      title: t ? t('CONTROL_NAME') : 'Control Name',
      dataIndex: 'controlName',
      key: 'controlName',
      width: 200,
      fixed: 'left',
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('controlName'),
      render: (value: string, record, index) => {
        if (record.notUsed) {
          return (
            <Tooltip title='Disabled due to a previous Risk/Control type change.'>
              <Styled.tooltipTR>{value || '—'}</Styled.tooltipTR>
            </Tooltip>
          );
        }
        return (
          <>
            <Styled.GlobalTooltipStyles />
            <Tooltip
              title={
                value
                  ? value.substring(0, 50) + (value.length > 50 ? '...' : '')
                  : ''
              }
              placement='topLeft'
              mouseEnterDelay={0.1}
              mouseLeaveDelay={0.1}
              arrow={false}
            >
              <Styled.ResizableTextArea
                value={value || ''}
                placeholder='Enter control name'
                rows={2}
                disabled={shouldDisable(record, 'controlName')}
                onChange={(e) => {
                  const newControls = [...controls];
                  const actualIndex = newControls.findIndex(
                    (c) => c.id === record.id
                  );
                  if (actualIndex !== -1) {
                    newControls[actualIndex].controlName = e.target.value;
                    trackingSetControls(newControls);
                  }
                }}
              />
            </Tooltip>
          </>
        );
      }
    },
    {
      title: t ? t('DESCRIPTION') : 'Description',
      dataIndex: 'controlDescription',
      key: 'controlDescription',
      width: 250,
      fixed: 'left',
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('controlDescription'),
      render: (value: string, record, index) => (
        <>
          <Styled.GlobalTooltipStyles />
          <Tooltip
            title={
              value
                ? value.substring(0, 50) + (value.length > 50 ? '...' : '')
                : ''
            }
            placement='topLeft'
            arrow={false}
          >
            <Styled.ResizableTextArea
              value={value || ''}
              placeholder='Enter control description'
              rows={3}
              disabled={shouldDisable(record, 'controlDescription')}
              onChange={(e) => {
                const newControls = [...controls];
                const actualIndex = newControls.findIndex(
                  (c) => c.id === record.id
                );
                if (actualIndex !== -1) {
                  newControls[actualIndex].controlDescription = e.target.value;
                  trackingSetControls(newControls);
                }
              }}
            />
          </Tooltip>
        </>
      )
    },
    {
      title: t ? t('TEST_OBJECTIVE_2ND_LINE') : 'Test Objective (2nd line)',
      dataIndex: 'testObjectiveness',
      key: 'testObjectiveness',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('testObjectiveness'),
      render: (value: string | LookupValue, record, index) => (
        <>
          <Styled.GlobalTooltipStyles />
          <Tooltip
            title={
              typeof value === 'string'
                ? value.substring(0, 50) + (value.length > 50 ? '...' : '')
                : value?.name
                ? value.name.substring(0, 50) +
                  (value.name.length > 50 ? '...' : '')
                : ''
            }
            placement='topLeft'
            mouseEnterDelay={0.1}
            mouseLeaveDelay={0.1}
            arrow={false}
          >
            <Styled.ResizableTextArea
              value={typeof value === 'string' ? value : value?.name || ''}
              placeholder='Enter test objective'
              disabled={shouldDisable(record, 'testObjectiveness')}
              onChange={(e) => {
                const newControls = [...controls];
                const actualIndex = newControls.findIndex(
                  (c) => c.id === record.id
                );
                if (actualIndex !== -1) {
                  newControls[actualIndex].testObjectiveness = e.target.value;
                  trackingSetControls(newControls);
                }
              }}
            />
          </Tooltip>
        </>
      )
    },
    {
      title: t ? t('TEST_DESCRIPTION') : 'Test Description',
      dataIndex: 'testDescription',
      key: 'testDescription',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('testDescription'),
      render: (value: string, record, index) => (
        <>
          <Styled.GlobalTooltipStyles />
          <Tooltip
            title={
              value
                ? value.substring(0, 50) + (value.length > 50 ? '...' : '')
                : ''
            }
            placement='topLeft'
            mouseEnterDelay={0.1}
            mouseLeaveDelay={0.1}
            arrow={false}
          >
            <Styled.ResizableTextArea
              value={value}
              placeholder='Enter test description'
              rows={3}
              disabled={shouldDisable(record, 'testDescription')}
              onChange={(e) => {
                const newControls = [...controls];
                const actualIndex = newControls.findIndex(
                  (c) => c.id === record.id
                );
                if (actualIndex !== -1) {
                  newControls[actualIndex].testDescription = e.target.value;
                  trackingSetControls(newControls);
                }
              }}
            />
          </Tooltip>
        </>
      )
    },
    {
      title: t ? t('TESTING_METHOD_2ND_LINE') : 'Testing method (2nd line)',
      dataIndex: 'testingMethod',
      key: 'testingMethod',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('testingMethod'),
      render: (value, record, index) => (
        <CustomSelect
          value={value?.id ?? undefined}
          options={testingMethod}
          disabled={shouldDisable(record, 'testingMethod')}
          placeholder='Select Testing Method'
          onChange={(id: number) => {
            const selected = testingMethod.find((o) => o.value === id);
            const newControls = [...controls];
            const actualIndex = newControls.findIndex(
              (c) => c.id === record.id
            );
            if (actualIndex !== -1) {
              newControls[actualIndex].testingMethod = {
                id,
                name: selected?.label ?? null
              };
              trackingSetControls(newControls);
            }
          }}
        />
      )
    },
    {
      title: t ? t('EVIDENCE_DESCRIPTION') : 'Evidence Description',
      dataIndex: 'evidenceDescription',
      key: 'evidenceDescription',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('evidenceDescription'),
      render: (value: string, record, index) => (
        <>
          <Styled.GlobalTooltipStyles />
          <Tooltip
            title={
              value
                ? value.substring(0, 50) + (value.length > 50 ? '...' : '')
                : ''
            }
            placement='topLeft'
            mouseEnterDelay={0.1}
            mouseLeaveDelay={0.1}
            arrow={false}
          >
            <Styled.ResizableTextArea
              value={value}
              placeholder='Enter evidence description'
              rows={3}
              disabled={shouldDisable(record, 'evidenceDescription')}
              onChange={(e) => {
                const newControls = [...controls];
                const actualIndex = newControls.findIndex(
                  (c) => c.id === record.id
                );
                if (actualIndex !== -1) {
                  newControls[actualIndex].evidenceDescription = e.target.value;
                  trackingSetControls(newControls);
                }
              }}
            />
          </Tooltip>
        </>
      )
    },
    {
      title: t ? t('EVIDENCE_FILE_1') : 'Evidence File 1',
      dataIndex: 'evidenceFile1',
      key: 'evidenceFile1',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('evidenceFile1'),
      render: (value: string, record, index) => {
        const isDisabled = shouldDisable(record, 'evidenceFile1');
        return (
          <CustomFileUpload
            defaultFileList={value ? [value] : []}
            onChange={(fileList) => {
              if (isDisabled) return;
              const newControls = [...controls];
              const actualIndex = newControls.findIndex(
                (c) => c.id === record.id
              );
              if (actualIndex !== -1) {
                newControls[actualIndex].evidenceFile1 =
                  fileList.length > 0
                    ? fileList[0].url || fileList[0].name
                    : null;
                trackingSetControls(newControls);
              }
            }}
          />
        );
      }
    },
    {
      title: t ? t('PERFORMER_TOD_2ND_LINE') : 'Performer ToD (2nd line)',
      dataIndex: 'performerToD',
      key: 'performerToD',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('performerToD'),
      render: (value: string | LookupValue, record, index) => (
        <>
          <Styled.GlobalTooltipStyles />
          <Tooltip
            title={
              typeof value === 'string'
                ? value.substring(0, 50) + (value.length > 50 ? '...' : '')
                : value?.name
                ? value.name.substring(0, 50) +
                  (value.name.length > 50 ? '...' : '')
                : ''
            }
            placement='topLeft'
            mouseEnterDelay={0.1}
            mouseLeaveDelay={0.1}
            arrow={false}
          >
            <Styled.ResizableTextArea
              value={typeof value === 'string' ? value : value?.name || ''}
              placeholder='Enter performer ToD'
              disabled={shouldDisable(record, 'performerToD')}
              onChange={(e) => {
                const newControls = [...controls];
                const actualIndex = newControls.findIndex(
                  (c) => c.id === record.id
                );
                if (actualIndex !== -1) {
                  newControls[actualIndex].performerToD = e.target.value;
                  trackingSetControls(newControls);
                }
              }}
            />
          </Tooltip>
        </>
      )
    },
    {
      title: t ? t('APPROVER_2ND_LINE') : 'Approver (2nd line)',
      dataIndex: 'approvers',
      key: 'approvers',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('approvers'),
      render: (value: string | LookupValue, record, index) => (
        <>
          <Styled.GlobalTooltipStyles />
          <Tooltip
            title={
              typeof value === 'string'
                ? value.substring(0, 50) + (value.length > 50 ? '...' : '')
                : value?.name
                ? value.name.substring(0, 50) +
                  (value.name.length > 50 ? '...' : '')
                : ''
            }
            placement='topLeft'
            mouseEnterDelay={0.1}
            mouseLeaveDelay={0.1}
            arrow={false}
          >
            <Styled.ResizableTextArea
              value={typeof value === 'string' ? value : value?.name || ''}
              placeholder='Enter approver'
              disabled={shouldDisable(record, 'approvers')}
              onChange={(e) => {
                const newControls = [...controls];
                const actualIndex = newControls.findIndex(
                  (c) => c.id === record.id
                );
                if (actualIndex !== -1) {
                  newControls[actualIndex].approvers = e.target.value;
                  trackingSetControls(newControls);
                }
              }}
            />
          </Tooltip>
        </>
      )
    },
    {
      title: t
        ? t('DESIGN_EFFECTIVENESS_2ND_LINE')
        : 'Design Effectiveness (2nd line)',
      dataIndex: 'designEffectiveness',
      key: 'designEffectiveness',
      width: 120,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('designEffectiveness'),
      render: (value, record, index) => (
        <CustomSelect
          value={value?.id ?? undefined}
          options={designEffectiveness}
          placeholder='Select Design Effectiveness'
          disabled={shouldDisable(record, 'designEffectiveness')}
          onChange={(id: number) => {
            const selected = designEffectiveness.find((o) => o.value === id);
            const newControls = [...controls];
            const actualIndex = newControls.findIndex(
              (c) => c.id === record.id
            );
            if (actualIndex !== -1) {
              newControls[actualIndex].designEffectiveness = {
                id,
                name: selected?.label ?? null
              };
              trackingSetControls(newControls);
            }
          }}
        />
      )
    },
    {
      title: t ? t('KEY_CONTROL') : 'Key Control',
      dataIndex: 'keyControl',
      key: 'keyControl',
      width: 120,
      ellipsis: true,
      render: (value: boolean) => (
        <Styled.KeyControlInput
          value={value ? 'true' : 'false'}
          disabled
          size='small'
        />
      )
    },
    {
      title: t
        ? t('LAST_TEST_DATE_DESIGN_2ND_LINE')
        : 'Last Test Date Design (2nd line)',
      dataIndex: 'lastTestDateDesign',
      key: 'lastTestDateDesign',
      width: 120,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('lastTestDateDesign'),
      render: (value: string | null, record, index) => (
        <CustomDatePicker
          value={value ? dayjs(value) : null}
          disabled={shouldDisable(record, 'lastTestDateDesign')}
          onChange={(date: dayjs.Dayjs | null) => {
            const newControls = [...controls];
            const actualIndex = newControls.findIndex(
              (c) => c.id === record.id
            );
            if (actualIndex !== -1) {
              newControls[actualIndex].lastTestDateDesign = date
                ? date.format('YYYY-MM-DD')
                : null;
              trackingSetControls(newControls);
            }
          }}
        />
      )
    }
  ];
};
